import { useEffect, useMemo, useRef, useState } from "react";
import { Send, Sparkles, ExternalLink, BadgeCheck, Bookmark } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PopuIcon from "@/components/PopuIcon";

function buildSuggestions(articles) {
  if (!articles || articles.length === 0) {
    return [
      "¿Qué noticias destacadas hay hoy?",
      "Resumime lo más importante",
      "¿De qué se habla en deportes?",
    ];
  }
  const out = [];
  const a0 = articles[0];
  if (a0) out.push(`¿De qué trata "${(a0.title || "").slice(0, 48)}${(a0.title || "").length > 48 ? "..." : ""}"?`);
  const a1 = articles[1];
  if (a1) out.push(`Más sobre ${(a1.title || "").split(":")[0].slice(0, 40)}`);
  out.push("Resumime las últimas noticias");
  return out.slice(0, 3);
}

function ThinkingAnimation() {
  return (
    <div className="flex items-center gap-3" data-testid="popu-ia-thinking">
      <div className="w-9 h-9 rounded-full flex items-center justify-center accent-bg text-white shrink-0 relative">
        <Sparkles size={16} strokeWidth={2.5} className="animate-pulse" />
        <span className="absolute inset-0 rounded-full accent-bg opacity-30 blur-md animate-ping" />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold" style={{ color: "var(--text-muted)" }}>
          POPU IA está pensando
        </span>
        <span className="flex gap-1 ml-1">
          <span className="popu-think-dot" style={{ animationDelay: "0ms" }} />
          <span className="popu-think-dot" style={{ animationDelay: "150ms" }} />
          <span className="popu-think-dot" style={{ animationDelay: "300ms" }} />
        </span>
      </div>
    </div>
  );
}

function ChatBubble({ entry }) {
  return (
    <div
      className="rounded-md p-4 md:p-5 border popu-ia-bubble-in"
      style={{ background: "var(--bg)", borderColor: "var(--border)" }}
      data-testid={`popu-ia-msg-${entry.id}`}
    >
      {/* User question */}
      <div className="flex items-start gap-3 mb-3 pb-3 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="w-8 h-8 rounded-full bg-[color:var(--surface-2)] flex items-center justify-center text-xs font-bold shrink-0" style={{ color: "var(--text-muted)" }}>
          TÚ
        </div>
        <p className="flex-1 font-semibold text-sm leading-relaxed pt-1" style={{ color: "var(--text)" }}>
          {entry.question}
        </p>
      </div>

      {/* AI answer */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full accent-bg flex items-center justify-center shrink-0 text-white">
          <Sparkles size={14} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm md:text-[15px] leading-relaxed whitespace-pre-line" style={{ color: "var(--text)" }}>
            {entry.answer}
          </p>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
            <div className="text-[11px] flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
              <span>Fuente: popu.news</span>
              <span>·</span>
              <span>Generado por IA</span>
            </div>
            <div className="flex items-center gap-3 text-xs font-semibold">
              {entry.related?.[0] && (
                <Link to={`/nota/${entry.related[0].slug}`} className="accent-text hover:underline flex items-center gap-1" data-testid={`popu-ia-ver-articulo-${entry.id}`}>
                  Ver artículo <ExternalLink size={12} />
                </Link>
              )}
              <button className="hover:accent-text transition" aria-label="Guardar"><Bookmark size={14} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PopuIABlock() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]); // newest first
  const [sessionId, setSessionId] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  const ask = async (q) => {
    if (!q.trim() || loading) return;
    setLoading(true);
    try {
      const res = await api.post("/popu-ia/ask", { question: q, session_id: sessionId });
      setSessionId(res.data.session_id);
      const entry = {
        id: Date.now(),
        question: q,
        answer: res.data.answer,
        related: res.data.related || [],
      };
      setMessages((prev) => [entry, ...prev]); // newest at top
    } catch (e) {
      const entry = {
        id: Date.now(),
        question: q,
        answer: "Hubo un problema con POPU IA. Probá de nuevo en unos segundos.",
        related: [],
      };
      setMessages((prev) => [entry, ...prev]);
    } finally {
      setLoading(false);
    }
  };

  const autoAskedRef = useRef(false);

  useEffect(() => {
    if (autoAskedRef.current) return;
    autoAskedRef.current = true;
    api.get("/articles", { params: { limit: 5 } }).then((r) => {
      const items = r.data.items || [];
      setRelatedArticles(items.slice(0, 3));
      if (items[0]) {
        ask(`Resumime esta nota: "${items[0].title}"`);
      } else {
        ask("¿Qué noticias destacadas hay hoy?");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suggestions = useMemo(() => buildSuggestions(relatedArticles), [relatedArticles]);

  const submit = (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setQuestion("");
    ask(q);
  };

  return (
    <section
      className="rounded-md border p-4 md:p-6 lg:p-8"
      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
      data-testid="popu-ia-block"
    >
      {/* Header */}
      <div className="flex items-center gap-3 md:gap-4 mb-4">
        <PopuIcon size={48} />
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-headline text-xl uppercase">POPU IA</span>
            <span className="overline accent-bg text-white px-2 py-0.5 rounded-sm" style={{ fontSize: "0.6rem" }}>BETA</span>
            <BadgeCheck size={16} className="accent-text" />
          </div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            Respuestas basadas en contenido de popu.news
          </p>
        </div>
      </div>

      {/* Single-line chat input (top, full width pill) */}
      <form
        onSubmit={submit}
        className="flex items-center gap-2 rounded-full border-2 px-2 pl-5 py-1.5 mb-3 focus-within:border-[color:var(--accent)] transition"
        style={{ background: "var(--bg)", borderColor: "var(--border)" }}
      >
        <input
          type="text"
          data-testid="popu-ia-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Escribí tu pregunta a POPU IA..."
          disabled={loading}
          className="flex-1 min-w-0 bg-transparent outline-none text-base placeholder:opacity-50 disabled:opacity-50 py-2"
          style={{ color: "var(--text)" }}
        />
        <button
          type="submit"
          data-testid="popu-ia-send"
          disabled={loading || !question.trim()}
          className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full accent-bg text-white font-bold text-sm disabled:opacity-40 transition hover:opacity-90"
          aria-label="Enviar"
        >
          <Send size={14} />
          <span className="hidden sm:inline">Enviar</span>
        </button>
      </form>

      {/* Suggestion chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {suggestions.map((s) => (
          <button
            key={s}
            data-testid={`popu-ia-chip-${s.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
            onClick={() => ask(s)}
            disabled={loading}
            className="chip disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>

      {/* Conversation feed (newest first) - scrollable */}
      <div
        className="space-y-3 overflow-y-auto pr-1"
        style={{ maxHeight: "55vh" }}
        data-testid="popu-ia-feed"
      >
        {loading && <ThinkingAnimation />}
        {messages.map((m) => (
          <ChatBubble key={m.id} entry={m} />
        ))}
      </div>

      {/* Related notes */}
      {relatedArticles.length > 0 && (
        <div className="mt-6">
          <p className="font-semibold mb-3 text-sm" style={{ color: "var(--text)" }}>
            Notas relacionadas
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {relatedArticles.slice(0, 3).map((r) => (
              <Link
                key={r.id || r.slug}
                to={`/nota/${r.slug}`}
                className="flex gap-3 p-3 rounded-sm border hover:border-[color:var(--accent)] transition"
                style={{ borderColor: "var(--border)", background: "var(--bg)" }}
                data-testid={`popu-ia-related-${r.slug}`}
              >
                <div
                  className="w-16 h-16 rounded-sm shrink-0 bg-cover bg-center"
                  style={{
                    background: r.featured_image ? `url(${r.featured_image}) center/cover` : "var(--surface-2)",
                  }}
                />
                <p className="text-sm font-semibold leading-tight line-clamp-3" style={{ color: "var(--text)" }}>
                  {r.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
